import React from 'react';
import type { JSX } from 'react';
import html2canvas from 'html2canvas';
import i18next from 'i18next';
import { getCaRTT } from '@/helpers/authUtils';
import store from '@/redux/store';
import styles from './ChatBotAI.module.scss';

type ChatMessage = {
  type: 'string' | 'object';
  content: string | JSX.Element | { props: { fileType: string; file: File; fileUrl: string } };
};

export class ChatBotAIProvider {
  private endpoint: string;
  public _injectMessage: ((message: JSX.Element, agent: string) => void) | null;
  private filesSelected: File[] = [];
  private clearAttachmentsCb: (() => void) | null = null;
  public includeScreenshot: boolean = false;
  public includePageURL: boolean = true;
  private _getPendingFiles?: () => File[];
  private _clearPendingFiles?: () => void;
  private t: ((key: string) => string) | null = null;

  private listeners = new Set<() => void>();

  public subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);

    const cleanup = () => {
      this.listeners.delete(cb);
    };

    return cleanup;
  };

  public injectMessage = (message: JSX.Element, agent: string) => {
    if (this._injectMessage) {
      this._injectMessage(message, agent);
    }
  };

  private emit = () => {
    this.listeners.forEach((cb) => cb());
  };

  public canForgetMemory: boolean = false;

  public setIncludeScreenshot(include: boolean) {
    this.includeScreenshot = include;
  }

  public setTranslator(fn: (key: string) => string) {
    this.t = fn;
  }

  private translate(key: string): string {
    return this.t ? this.t(`cartAI.${key}`) : i18next.t(`cartAI.${key}`); // fallback
  }

  constructor(endpoint: string) {
    this.endpoint = endpoint;
    this._injectMessage = null;
  }

  public isWindowHalfScreen: boolean = false;
  private windowSizeChangedCb: (() => void) | null = null;

  public onWindowSizeChanged(cb: () => void): (() => void) {
    this.windowSizeChangedCb = cb;

    // Return cleanup function
    const cleanup = () => {
      if (this.windowSizeChangedCb === cb) {
        this.windowSizeChangedCb = null;
      }
    };

    return cleanup;
  }

  public toggleWindowHalfScreen() {
    this.isWindowHalfScreen = !this.isWindowHalfScreen;
    this.windowSizeChangedCb?.();
  }

  setGetPendingFiles(fn: () => File[]) { this._getPendingFiles = fn; }
  setClearPendingFiles(fn: () => void) { this._clearPendingFiles = fn; }

  public setFilesSelected(files: File[]) {
    this.filesSelected = files || [];
  }

  public setClearAttachmentsCb(cb: () => void) {
    this.clearAttachmentsCb = cb;
  }

  public setInjectors(injectMessage) {
    if (!this._injectMessage) this._injectMessage = injectMessage;
  }

  public async* sendMessages(messages: ChatMessage[]): AsyncGenerator<string> {
    const lastMessage = messages[messages.length - 1];

    const form = new FormData();

    // if text message
    if (lastMessage?.type === 'string' && lastMessage.content) {
      form.append('input', lastMessage.content as string);
    }

    // if audio message
    if (lastMessage?.type === 'object'
      && typeof lastMessage.content === 'object'
      && lastMessage.content !== null
      && 'props' in lastMessage.content
      && lastMessage.content.props?.fileType === 'audio'
      && lastMessage.content.props?.file instanceof File) {
      const audioFile: File = lastMessage.content.props.file;

      this.injectMessage((<div className={styles.ChatAudioBubble}><audio controls src={lastMessage.content.props.fileUrl} /></div>), 'user');
      form.append('audio', audioFile, audioFile.name || 'voice-input.wav');
    }

    // if files attached
    const filesSelected = this._getPendingFiles?.() || [];

    for (const f of filesSelected) {
      form.append('files', f, f.name);

      this.injectMessage((<div className={`rcb-user-message rcb-user-message-entry ${styles.ChatFileSent}`}>📎{ f.name }</div>), 'user');
    }

    // if Screenshot needed (opcional)
    if (this.includeScreenshot) {
      const root = document.getElementById('root');
      const chatBotWindow = document.getElementById('window-chatbot');

      if (root) {
        const canvas = await html2canvas(root, { ignoreElements: (element) => // Ignore the chatbot window in the screenshot
          Boolean(element === chatBotWindow || chatBotWindow?.contains(element)),
        });
        const blob = await (await fetch(canvas.toDataURL('image/png'))).blob();
        form.append('files', new File([blob], 'screenshot.png', { type: 'image/png' }));

        this.injectMessage((<div className={`rcb-user-message rcb-user-message-entry ${styles.ChatScreenshotSent}`}>📸 {this.translate('screenshotSent')}</div>), 'user');
      }
    }

    // if is needed page URL
    if (this.includePageURL) {
      form.append('pageUrl', window.location.href);
    }

    // minimal validation
    if (!form.has('input') && !form.has('audio') && !form.has('files')) {
      throw new Error('No input, audio or files to send');
    }

    // sent request
    const resp = await fetch(`${this.endpoint}/chat`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getCaRTT()}` },
      body: form,
    });

    this.filesSelected = [];
    this.clearAttachmentsCb?.();
    this._clearPendingFiles?.();

    if (!resp.ok) {
      throw new Error(`Backend error: ${resp.status}`);
    }

    this.canForgetMemory = true;
    this.emit();

    try {
      let rawResponse = await resp.json();
      rawResponse = rawResponse.content;

      // si parece HTML, lo pintamos con dangerouslySetInnerHTML
      if (rawResponse.startsWith('<!doctype html') || rawResponse.startsWith('<html')) {
        this.injectMessage(<div className={`rcb-bot-message ${styles.NormalBotResponse}`} dangerouslySetInnerHTML={{ __html: rawResponse }} />, 'bot');
        yield '';

        return;
      }
    } catch {
      // Error parsing JSON, trying as text
      const text = await resp.text();
      yield text;

      return;
    }
  }

  public forgetMemory = async (): Promise<{ message: JSX.Element; success: boolean }> => {
    this.injectMessage((<div className={`rcb-bot-message rcb-bot-message-entry ${styles.ForgettingMemoryChat}`}>🗑️ {this.translate('forgettingMemory')}</div>), 'bot');

    try {
      const resp = await fetch(`${this.endpoint}/chat/session/reset`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getCaRTT()}` },
      });

      if (!resp.ok) {
        throw new Error(`Backend error: ${resp.status}`);
      }

      const userId = store.getState().userReducer?.userCode;

      if (userId) {
        const storageKey = `cart_chat_history_${userId}`;
        localStorage.removeItem(storageKey);
      }

      this.canForgetMemory = false;
      this.emit();

      const successMessage = (<div className={`rcb-bot-message rcb-bot-message-entry ${styles.MemoryForgottenChat}`}>✅ {this.translate('memoryForgotten')}</div>);

      return { message: successMessage, success: true };
    } catch (e) {
      const failMessage = (<div className={`rcb-bot-message rcb-bot-message-entry ${styles.MemoryForgetError}`}>❌ Error forgetting memory: {(e as Error).message}</div>);

      return { message: failMessage, success: false };
    }
  };
}
